import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy, limit, where, Timestamp, getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import styles from './AdminAnalyticsDashboard.module.css';
import instagramIcon from '../assets/images/logo_instagram.png';
import whatsappIcon from '../assets/images/logo_whatsapp.png';
import tiktokIcon from '../assets/images/tiktok_logo.png';
import linkIcon from '../assets/images/logo_personal_link.png';

function AdminAnalyticsDashboard() {
  const { currentUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pageViews, setPageViews] = useState([]);
  const [buttonClicks, setButtonClicks] = useState([]);
  const [trafficSources, setTrafficSources] = useState([]);
  const [searchStats, setSearchStats] = useState({
    totalSearchViews: 0,
    totalProfileOpens: 0,
    totalContactClicks: 0,
    topFilters: [],
    topProfiles: [],
    contactClicks: [],
    noResultSearches: 0,
    totalSearches: 0,
  });
  // timeRange en heures (par ex. 1h, 3h, 12h, 24h, 7j, 30j, 90j)
  const [timeRangeHours, setTimeRangeHours] = useState(24); // Par défaut : 24 dernières heures
  const [activeTab, setActiveTab] = useState('dashboard'); // 'dashboard' | 'details'
  const [expandedProfiles, setExpandedProfiles] = useState(false); // Pour collapse/expand de la liste complète

  // Protection admin
  useEffect(() => {
    if (!authLoading && (!currentUser || currentUser.displayName !== 'admin')) {
      navigate('/');
    }
  }, [currentUser, authLoading, navigate]);

  useEffect(() => {
    if (!currentUser || currentUser.displayName !== 'admin') return;

    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        const now = new Date();
        const startDate = new Date(now.getTime() - timeRangeHours * 60 * 60 * 1000);

        // Récupérer tous les événements de navigation / CTA
        const eventsQuery = query(
          collection(db, 'pageEvents'),
          where('createdAt', '>=', Timestamp.fromDate(startDate)),
          orderBy('createdAt', 'desc'),
          limit(10000) // Limite haute pour avoir toutes les données
        );

        const snapshot = await getDocs(eventsQuery);
        const events = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Analyser les pages vues
        const pageViewsMap = new Map();
        events.forEach((event) => {
          if (event.eventType === 'page_view') {
            const page = event.page || 'unknown';
            const current = pageViewsMap.get(page) || { page, count: 0, uniqueUsers: new Set() };
            current.count++;
            if (event.userId) {
              current.uniqueUsers.add(event.userId);
            }
            pageViewsMap.set(page, current);
          }
        });

        const pageViewsList = Array.from(pageViewsMap.values())
          .map((item) => ({
            page: item.page,
            count: item.count,
            uniqueUsers: item.uniqueUsers.size,
          }))
          .sort((a, b) => b.count - a.count);

        // Analyser les clics sur boutons
        const buttonClicksMap = new Map();
        events.forEach((event) => {
          if (
            event.eventType === 'cta_click' ||
            event.eventType === 'favorite_added' ||
            event.eventType === 'favorite_removed' ||
            event.eventType === 'contact_clicked' ||
            event.eventType === 'signup_started' ||
            event.eventType === 'signup_completed' ||
            event.eventType === 'payment_started' ||
            event.eventType === 'payment_success'
          ) {
            const buttonName = event.metadata?.button || event.metadata?.cta || event.eventType;
            const current = buttonClicksMap.get(buttonName) || {
              button: buttonName,
              count: 0,
              uniqueUsers: new Set(),
            };
            current.count++;
            if (event.userId) {
              current.uniqueUsers.add(event.userId);
            }
            buttonClicksMap.set(buttonName, current);
          }
        });

        const buttonClicksList = Array.from(buttonClicksMap.values())
          .map((item) => ({
            button: item.button,
            count: item.count,
            uniqueUsers: item.uniqueUsers.size,
          }))
          .sort((a, b) => b.count - a.count);

        // Analyser les sources de trafic
        const trafficSourcesMap = new Map();
        events.forEach((event) => {
          if (event.eventType === 'page_view') {
            const source = event.metadata?.source || 'unknown';
            const current = trafficSourcesMap.get(source) || {
              source,
              count: 0,
              uniqueUsers: new Set(),
            };
            current.count++;
            if (event.userId) {
              current.uniqueUsers.add(event.userId);
            }
            trafficSourcesMap.set(source, current);
          }
        });

        const trafficSourcesList = Array.from(trafficSourcesMap.values())
          .map((item) => ({
            source: item.source,
            count: item.count,
            uniqueUsers: item.uniqueUsers.size,
          }))
          .sort((a, b) => b.count - a.count);

        setPageViews(pageViewsList);
        setButtonClicks(buttonClicksList);
        setTrafficSources(trafficSourcesList);

        // --- Récupérer les stats détaillées pour les coiffeuses (hairstylistStats) ---
        const hsQuery = query(
          collection(db, 'hairstylistStats'),
          where('event_date', '>=', Timestamp.fromDate(startDate)),
        );

        const hsSnapshot = await getDocs(hsQuery);
        const hsEvents = hsSnapshot.docs.map((snap) => ({
          id: snap.id,
          ...snap.data(),
        }));

        // Compter les vues de recherche
        let totalSearchViews = 0;
        let totalProfileOpens = 0;
        let totalContactClicks = 0;
        const filtersMap = new Map();
        const profileViewsMap = new Map();
        const contactClicksMap = new Map();
        const contactByHairstylistMap = new Map();

        hsEvents.forEach((evt) => {
          const type = evt.event_type;
          const hairstylistId = evt.hairstylistId;
          const meta = evt.meta || {};

          if (type === 'search_view') {
            totalSearchViews += 1;
            const filters = Array.isArray(meta.search_filters) ? meta.search_filters : [];
            filters.forEach((f) => {
              const key = String(f);
              const current = filtersMap.get(key) || 0;
              filtersMap.set(key, current + 1);
            });
          }

          if (type === 'profile_open') {
            if (!hairstylistId) return;
            totalProfileOpens += 1;
            const current = profileViewsMap.get(hairstylistId) || 0;
            profileViewsMap.set(hairstylistId, current + 1);
          }

          if (
            type === 'contact_instagram_click' ||
            type === 'contact_whatsapp_click' ||
            type === 'contact_tiktok_click' ||
            type === 'contact_link_click'
          ) {
            totalContactClicks += 1;
            const contactType = meta.contact_type || type.replace('contact_', '').replace('_click', '');
            // Agrégation globale
            const current = contactClicksMap.get(contactType) || 0;
            contactClicksMap.set(contactType, current + 1);

            // Agrégation par coiffeuse
            if (hairstylistId) {
              const existing = contactByHairstylistMap.get(hairstylistId) || {
                instagram: 0,
                whatsapp: 0,
                tiktok: 0,
                link: 0,
              };
              if (contactType === 'instagram') existing.instagram += 1;
              else if (contactType === 'whatsapp') existing.whatsapp += 1;
              else if (contactType === 'tiktok') existing.tiktok += 1;
              else existing.link += 1;
              contactByHairstylistMap.set(hairstylistId, existing);
            }
          }
        });

        // Top filtres utilisés
        const topFilters = Array.from(filtersMap.entries())
          .map(([filter, count]) => ({ filter, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 10);

        // TOUTES les coiffeuses visitées (triées par nombre de vues décroissant)
        const allProfilesRaw = Array.from(profileViewsMap.entries())
          .map(([hairstylistId, count]) => ({ hairstylistId, count }))
          .sort((a, b) => b.count - a.count);

        let allProfiles = [];
        if (allProfilesRaw.length > 0) {
          const ids = allProfilesRaw.map((p) => p.hairstylistId);
          const hsDocs = await Promise.all(
            ids.map((id) => getDoc(doc(db, 'hairstylists', id))),
          );
          allProfiles = allProfilesRaw.map((p, index) => {
            const snap = hsDocs[index];
            const data = snap.exists() ? snap.data() : {};
            const contactStats = contactByHairstylistMap.get(p.hairstylistId) || {
              instagram: 0,
              whatsapp: 0,
              tiktok: 0,
              link: 0,
            };
            return {
              hairstylistId: p.hairstylistId,
              name: data.name || p.hairstylistId,
              city: data.mainCity || data.city || '',
              count: p.count,
              contacts: contactStats,
            };
          });
        }

        // Clics de contact agrégés
        const contactClicks = Array.from(contactClicksMap.entries())
          .map(([contactType, count]) => ({ contactType, count }))
          .sort((a, b) => b.count - a.count);

        setSearchStats({
          totalSearchViews,
          totalProfileOpens,
          totalContactClicks,
          topFilters,
          topProfiles: allProfiles, // Maintenant contient TOUTES les coiffeuses visitées
          contactClicks,
          noResultSearches: 0, // placeholder pour futur usage si on logge les no-result dans pageEvents
          totalSearches: totalSearchViews, // ici assimilé aux search_view
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [currentUser, timeRangeHours]);

  if (authLoading || loading) {
    return (
      <div className={styles.dashboard}>
        <div className={styles.loading}>Chargement...</div>
      </div>
    );
  }

  if (!currentUser || currentUser.displayName !== 'admin') {
    return null;
  }

  return (
    <div className={styles.dashboard}>
      <div className={styles.container}>
        <header className={styles.header}>
          <h1 className={styles.title}>📊 Dashboard Analytics</h1>
          <p className={styles.subtitle}>Statistiques d'utilisation de l'application</p>
        </header>

        {/* Filtre de période */}
        <div className={styles.filters}>
          <label htmlFor="dateRange" className={styles.filterLabel}>
            Période :
          </label>
          <select
            id="dateRange"
            className={styles.filterSelect}
            value={timeRangeHours}
            onChange={(e) => setTimeRangeHours(parseInt(e.target.value, 10))}
          >
            <option value={1}>Dernière heure (1h)</option>
            <option value={3}>3 dernières heures</option>
            <option value={12}>12 dernières heures</option>
            <option value={24}>24 dernières heures</option>
            <option value={7 * 24}>7 derniers jours</option>
            <option value={30 * 24}>30 derniers jours</option>
            <option value={90 * 24}>90 derniers jours</option>
          </select>
        </div>

        {/* Onglets vue Dashboard / Détails bruts */}
        <div className={styles.tabs}>
          <button
            type="button"
            className={`${styles.tabButton} ${activeTab === 'dashboard' ? styles.tabButtonActive : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            Vue dashboard
          </button>
          <button
            type="button"
            className={`${styles.tabButton} ${activeTab === 'details' ? styles.tabButtonActive : ''}`}
            onClick={() => setActiveTab('details')}
          >
            Détails bruts
          </button>
        </div>

        {activeTab === 'dashboard' && (
          <>
            {/* Vue d'ensemble rapide */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Résumé global</h2>
              <div className={styles.kpiRow}>
                <div className={styles.kpiCard}>
                  <p className={styles.kpiLabel}>Vues de la page de recherche</p>
                  <p className={styles.kpiValue}>
                    {(() => {
                      const searchPage = pageViews.find((p) => p.page === '/search');
                      return searchPage ? searchPage.count : 0;
                    })()}
                  </p>
                </div>
                <div className={styles.kpiCard}>
                  <p className={styles.kpiLabel}>Vues du forum</p>
                  <p className={styles.kpiValue}>
                    {(() => {
                      const forumPage = pageViews.find((p) => p.page === '/posts');
                      return forumPage ? forumPage.count : 0;
                    })()}
                  </p>
                </div>
                <div className={styles.kpiCard}>
                  <p className={styles.kpiLabel}>Apparitions dans la recherche</p>
                  <p className={styles.kpiValue}>{searchStats.totalSearchViews}</p>
                </div>
                <div className={styles.kpiCard}>
                  <p className={styles.kpiLabel}>Conversion recherche → profil</p>
                  <p className={styles.kpiValue}>
                    {searchStats.totalSearchViews > 0
                      ? `${Math.round((searchStats.totalProfileOpens / searchStats.totalSearchViews) * 100)}%`
                      : '—'}
                  </p>
                </div>
                <div className={styles.kpiCard}>
                  <p className={styles.kpiLabel}>Conversion profil → contact</p>
                  <p className={styles.kpiValue}>
                    {searchStats.totalProfileOpens > 0
                      ? `${Math.round((searchStats.totalContactClicks / searchStats.totalProfileOpens) * 100)}%`
                      : '—'}
                  </p>
                </div>
              </div>
            </section>

            {/* Profils coiffeuses visitées – vue dashboard (TOUTES avec collapse/expand) */}
            <section className={styles.section}>
              <div className={styles.sectionHeaderWithAction}>
                <h2 className={styles.sectionTitle}>
                  Profils coiffeuses visitées ({searchStats.topProfiles?.length || 0})
                </h2>
                {searchStats.topProfiles && searchStats.topProfiles.length > 20 && (
                  <button
                    type="button"
                    className={styles.expandButton}
                    onClick={() => setExpandedProfiles(!expandedProfiles)}
                  >
                    {expandedProfiles ? 'Réduire' : `Voir tout (${searchStats.topProfiles.length})`}
                  </button>
                )}
              </div>
              {(!searchStats.topProfiles || searchStats.topProfiles.length === 0) ? (
                <div className={styles.emptyState}>
                  <p>Aucune donnée de visites de profil pour cette période</p>
                </div>
              ) : (
                <>
                  <div className={styles.profileGrid}>
                    {(expandedProfiles
                      ? searchStats.topProfiles
                      : searchStats.topProfiles.slice(0, 20)
                    ).map((item) => (
                      <div key={item.hairstylistId} className={styles.profileCard}>
                        <div className={styles.profileHeader}>
                          <div>
                            <div className={styles.profileName}>{item.name}</div>
                            <div className={styles.profileCity}>{item.city || 'Ville non renseignée'}</div>
                          </div>
                          <div className={styles.profileViewsBlock}>
                            <div className={styles.profileViewsLabel}>Ouvertures</div>
                            <div className={styles.profileViewsValue}>{item.count}</div>
                          </div>
                        </div>
                        <div className={styles.profileContactsRow}>
                          <div className={styles.profileContactItem}>
                            <img src={instagramIcon} alt="Instagram" className={styles.contactIconSmall} />
                            <span className={styles.profileContactCount}>{item.contacts?.instagram || 0}</span>
                          </div>
                          <div className={styles.profileContactItem}>
                            <img src={whatsappIcon} alt="WhatsApp" className={styles.contactIconSmall} />
                            <span className={styles.profileContactCount}>{item.contacts?.whatsapp || 0}</span>
                          </div>
                          <div className={styles.profileContactItem}>
                            <img src={tiktokIcon} alt="TikTok" className={styles.contactIconSmall} />
                            <span className={styles.profileContactCount}>{item.contacts?.tiktok || 0}</span>
                          </div>
                          <div className={styles.profileContactItem}>
                            <img src={linkIcon} alt="Lien externe" className={styles.contactIconSmall} />
                            <span className={styles.profileContactCount}>{item.contacts?.link || 0}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {!expandedProfiles && searchStats.topProfiles.length > 20 && (
                    <div className={styles.expandHint}>
                      {searchStats.topProfiles.length - 20} autres coiffeuses masquées. Cliquez sur "Voir tout" pour les afficher.
                    </div>
                  )}
                </>
              )}
            </section>

            {/* Filtres de recherche les plus utilisés (vue synthétique) */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Filtres de recherche les plus utilisés</h2>
              {(!searchStats.topFilters || searchStats.topFilters.length === 0) ? (
                <div className={styles.emptyState}>
                  <p>Aucune donnée de filtres pour cette période</p>
                </div>
              ) : (
                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Filtre</th>
                        <th>Utilisations</th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchStats.topFilters.map((item, index) => (
                        <tr key={index}>
                          <td className={styles.filterCell}>{item.filter}</td>
                          <td className={styles.countCell}>{item.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Clics sur les moyens de contact (coiffeuses) – synthèse */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Clics sur les moyens de contact (coiffeuses)</h2>
              {(!searchStats.contactClicks || searchStats.contactClicks.length === 0) ? (
                <div className={styles.emptyState}>
                  <p>Aucune donnée de clics de contact pour cette période</p>
                </div>
              ) : (
                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Type de contact</th>
                        <th>Clics</th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchStats.contactClicks.map((item, index) => (
                        <tr key={index}>
                          <td className={styles.buttonCell}>
                            {item.contactType === 'instagram' ? 'Instagram' :
                             item.contactType === 'whatsapp' ? 'WhatsApp' :
                             item.contactType === 'tiktok' ? 'TikTok' :
                             'Autre lien'}
                          </td>
                          <td className={styles.countCell}>{item.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}

        {activeTab === 'details' && (
          <>
            {/* Pages explorées – liste brute */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>📄 Pages explorées (brut)</h2>
              {pageViews.length === 0 ? (
                <div className={styles.emptyState}>
                  <p>Aucune donnée de pages vues pour cette période</p>
                </div>
              ) : (
                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Page</th>
                        <th>Vues totales</th>
                        <th>Utilisateurs uniques</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageViews.map((item, index) => (
                        <tr key={index}>
                          <td className={styles.pageCell}>{item.page}</td>
                          <td className={styles.countCell}>{item.count}</td>
                          <td className={styles.countCell}>{item.uniqueUsers}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Boutons cliqués – liste brute */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>🖱️ Boutons principaux cliqués (brut)</h2>
              {buttonClicks.length === 0 ? (
                <div className={styles.emptyState}>
                  <p>Aucune donnée de clics pour cette période</p>
                </div>
              ) : (
                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Bouton / Action</th>
                        <th>Clics totaux</th>
                        <th>Utilisateurs uniques</th>
                      </tr>
                    </thead>
                    <tbody>
                      {buttonClicks.map((item, index) => (
                        <tr key={index}>
                          <td className={styles.buttonCell}>{item.button}</td>
                          <td className={styles.countCell}>{item.count}</td>
                          <td className={styles.countCell}>{item.uniqueUsers}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Sources de trafic – liste brute */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>🌐 Sources de trafic (brut)</h2>
              {trafficSources.length === 0 ? (
                <div className={styles.emptyState}>
                  <p>Aucune donnée de sources de trafic pour cette période</p>
                </div>
              ) : (
                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Source</th>
                        <th>Visites totales</th>
                        <th>Utilisateurs uniques</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trafficSources.map((item, index) => (
                        <tr key={index}>
                          <td className={styles.sourceCell}>
                            {item.source === 'direct' ? '🔗 Direct' : 
                             item.source === 'google' ? '🔍 Google' :
                             item.source === 'instagram' ? '📷 Instagram' :
                             item.source === 'facebook' ? '👥 Facebook' :
                             item.source === 'tiktok' ? '🎵 TikTok' :
                             item.source === 'twitter' ? '🐦 Twitter' :
                             item.source === 'linkedin' ? '💼 LinkedIn' :
                             item.source === 'youtube' ? '▶️ YouTube' :
                             item.source === 'whatsapp' ? '💬 WhatsApp' :
                             `🌐 ${item.source.charAt(0).toUpperCase() + item.source.slice(1)}`}
                          </td>
                          <td className={styles.countCell}>{item.count}</td>
                          <td className={styles.countCell}>{item.uniqueUsers}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Profils coiffeuses – détail brut */}
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Profils coiffeuses les plus visités (détail)</h2>
              {(!searchStats.topProfiles || searchStats.topProfiles.length === 0) ? (
                <div className={styles.emptyState}>
                  <p>Aucune donnée de visites de profil pour cette période</p>
                </div>
              ) : (
                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Coiffeuse</th>
                        <th>Ville</th>
                        <th>Ouvertures de profil</th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchStats.topProfiles.map((item) => (
                        <tr key={item.hairstylistId}>
                          <td className={styles.buttonCell}>{item.name}</td>
                          <td className={styles.sourceCell}>{item.city || '—'}</td>
                          <td className={styles.countCell}>{item.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

export default AdminAnalyticsDashboard;

